<template>
    <div class="automation-wizard-condition">
        <div v-if="isLoadingCustomFields" class="automation-wizard-condition__dashed-border">
            <div class="automation-wizard-condition__skeleton">
                <div class="automation-wizard-condition__skeleton-header">
                    <CompassSkeleton
                        :variant="CompassEnums.SkeletonVariantType.SQUARE"
                        :size="CompassEnums.SkeletonSize.MEDIUM"
                    />
                    <CompassSkeleton :size="CompassEnums.SkeletonSize.MEDIUM" />
                </div>
                <div class="automation-wizard-condition__skeleton-row">
                    <CompassSkeleton :size="CompassEnums.SkeletonSize.MEDIUM" />
                    <CompassSkeleton :size="CompassEnums.SkeletonSize.LARGE" />
                </div>
            </div>
        </div>
        <template v-else-if="rankedConditions.length > 0">
            <template
                v-for="(rankedCondition, index) in rankedConditions"
                :key="getConditionKey(rankedCondition.condition)"
            >
                <div
                    v-if="index > 0"
                    class="automation-wizard-condition__and-separator typographies-paragraph-caps-small-bold"
                >
                    {{ translations.andLabel }}
                </div>
                <AutomationWizardConditionRow
                    :modelValue="rankedCondition.condition"
                    :groupedFields="allGroupedFields"
                    @update:modelValue="
                        (val) => updateCondition(rankedCondition.originalIndex, val)
                    "
                    @remove="removeCondition(rankedCondition.originalIndex)"
                />
            </template>
            <!-- TODO: uncomment when multiple conditions are supported
            <CompassButton
                :type="CompassEnums.ButtonType.SECONDARY"
                leftIcon="plus"
                :label="translations.addCondition"
                :disabled="allAvailableFields.length === 0"
                @click="addCondition"
            />
            -->
        </template>
        <div v-else class="automation-wizard-condition__dashed-border">
            <div class="automation-wizard-condition__empty">
                <CompassColoredIcon
                    icon="filter"
                    :color="CompassEnums.ColoredIconColorType.PURPLE"
                    :size="CompassEnums.ColoredIconSize.LARGE"
                />
                <div class="automation-wizard-condition__empty-text">
                    <p
                        class="automation-wizard-condition__empty-title typographies-paragraph-medium-bold"
                    >
                        {{ translations.emptyTitle }}
                    </p>
                    <p
                        class="automation-wizard-condition__empty-subtitle typographies-paragraph-small"
                    >
                        {{ translations.emptySubtitle }}
                    </p>
                </div>
                <CompassButton
                    :type="CompassEnums.ButtonType.SECONDARY"
                    leftIcon="plus"
                    :label="translations.addCondition"
                    :disabled="allAvailableFields.length === 0"
                    @click="addCondition"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, inject } from 'vue';
import { Translator } from 'types/injects/translator.type';
import { PushToast } from 'types/injects/pushToast.type';
import { getAllCustomFields } from 'api/customfields.js';
import {
    SUPPORTED_CF_TYPES,
    ConditionScope,
    PROPERTY_GROUP_TO_CF_OBJECT,
} from 'Dictionaries/automation/conditionOperators';
import AutomationWizardConditionRow from 'vcomponents/automation/templates/Wizard/Condition/AutomationWizardConditionRow.vue';
import type {
    Condition,
    ConditionField,
    CustomField,
    FieldGroup,
    GroupedCustomFields,
    PropertyDefinition,
} from 'vcomponents/automation/templates/Wizard/Condition/types';

const OBJECT_TYPE_LABELS: Record<string, string> = {
    document: 'common.labels.document',
    opportunity: 'common.labels.opportunity',
    contact: 'common.labels.contact',
    client: 'common.labels.company',
    prospect: 'common.labels.prospect',
    supplier: 'common.labels.supplier',
    item: 'common.labels.item',
    service: 'common.labels.service',
    ticket: 'common.labels.ticket',
    task: 'common.labels.task',
    purchase: 'common.labels.purchase',
    project: 'common.labels.project',
    staff: 'common.labels.staff',
    site: 'common.labels.site',
};

interface Props {
    objectTypes: string[];
    propertyDefinitions?: PropertyDefinition[];
}

const props = withDefaults(defineProps<Props>(), {
    propertyDefinitions: () => [],
});

const conditions = defineModel<Condition[]>({ default: () => [] });

const $t = inject('$t') as Translator;
const $pushToast = inject('$pushToast') as PushToast;

const translations: Record<string, string> = {
    addCondition: $t.__('common.actions.add_condition'),
    emptyTitle: $t.__('automation.AutomationWizardCondition.empty_title'),
    emptySubtitle: $t.__('automation.AutomationWizardCondition.empty_subtitle'),
    andLabel: $t.__('common.labels.and'),
};

const isLoadingCustomFields = ref(false);
const groupedCustomFields = ref<GroupedCustomFields[]>([]);

// Assigns a stable unique key to each condition object for v-for :key tracking,
// without adding a technical property to the data model.
const conditionKeyMap = new WeakMap<Condition, number>();
let nextConditionKey = 0;

const resolvePropertyCfObject = (group: string): string =>
    PROPERTY_GROUP_TO_CF_OBJECT[group] || group;

const allGroupedFields = computed((): FieldGroup[] => {
    const groupMap = new Map<
        string,
        { label: string; properties: ConditionField[]; customFields: ConditionField[] }
    >();

    for (const cfGroup of groupedCustomFields.value) {
        groupMap.set(cfGroup.objectType, {
            label: cfGroup.label,
            properties: [],
            customFields: [...cfGroup.fields],
        });
    }

    for (const prop of props.propertyDefinitions) {
        const cfObjectType = resolvePropertyCfObject(prop.group);
        if (!groupMap.has(cfObjectType)) {
            const label = OBJECT_TYPE_LABELS[cfObjectType]
                ? $t.__(OBJECT_TYPE_LABELS[cfObjectType])
                : cfObjectType;
            groupMap.set(cfObjectType, { label, properties: [], customFields: [] });
        }
        groupMap.get(cfObjectType)!.properties.push({
            id: prop.id,
            name: prop.label,
            type: prop.dataType,
            group: prop.group,
            isProperty: true,
        });
    }

    const sortByName = (fieldA: ConditionField, fieldB: ConditionField): number =>
        fieldA.name.localeCompare(fieldB.name);

    return Array.from(groupMap.values())
        .map((group) => ({
            label: group.label,
            fields: [...group.properties.sort(sortByName), ...group.customFields.sort(sortByName)],
        }))
        .filter((group) => group.fields.length > 0);
});

const allAvailableFields = computed((): ConditionField[] =>
    allGroupedFields.value.flatMap((group) => group.fields),
);

const rankedConditions = computed((): { condition: Condition; originalIndex: number }[] =>
    conditions.value
        .map((condition, originalIndex) => ({ condition, originalIndex }))
        .filter(({ condition }) => !!condition.rank),
);

const getConditionKey = (condition: Condition): number => {
    if (!conditionKeyMap.has(condition)) {
        conditionKeyMap.set(condition, ++nextConditionKey);
    }
    return conditionKeyMap.get(condition)!;
};

const addCondition = (): void => {
    const nextRank = rankedConditions.value.length + 1;
    conditions.value = [
        ...conditions.value,
        { scope: null, id: null, dataType: null, operator: null, value: null, rank: nextRank },
    ];
};

const removeCondition = (index: number): void => {
    const condition = conditions.value[index];
    if (condition.scope === ConditionScope.OBJECT_PROPERTIES) {
        const updated = [...conditions.value];
        updated[index] = {
            id: condition.id,
            label: condition.label,
            scope: condition.scope,
            dataType: condition.dataType,
            group: condition.group,
            operator: null,
            value: null,
        };
        conditions.value = updated;
    } else {
        conditions.value = conditions.value.filter((_condition, idx) => idx !== index);
    }
};

const updateCondition = (index: number, value: Condition): void => {
    Object.assign(conditions.value[index], value);
    conditions.value = [...conditions.value];
};

const loadCustomFields = async (): Promise<void> => {
    isLoadingCustomFields.value = true;
    try {
        const results: CustomField[][] = await Promise.all(
            props.objectTypes.map((type) => getAllCustomFields(type)),
        );
        groupedCustomFields.value = props.objectTypes
            .map((type, index) => ({
                objectType: type,
                label: $t.__(OBJECT_TYPE_LABELS[type] || type),
                fields: results[index].filter((field: CustomField) =>
                    SUPPORTED_CF_TYPES.includes(field.type),
                ),
            }))
            .filter((group) => group.fields.length > 0);
    } catch {
        groupedCustomFields.value = [];
        $pushToast({
            type: 'error',
            icon: true,
            text: $t.__('common.labels.toast.error'),
        });
    } finally {
        isLoadingCustomFields.value = false;
    }
};

const removeUnavailableConditions = (): void => {
    const availableFieldIds = new Set(allAvailableFields.value.map((field) => field.id));
    conditions.value = conditions.value.filter(
        (condition) => !condition.id || availableFieldIds.has(condition.id),
    );
};
defineExpose({ removeUnavailableConditions });

onMounted(() => {
    loadCustomFields();
});
</script>

<style lang="scss" scoped>
.automation-wizard-condition {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-3);
    &__and-separator {
        text-align: center;
        color: var(--colors-common-text-subtle-default);
        text-transform: uppercase;
    }
    &__dashed-border {
        background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='8' ry='8' stroke='%23B690FF' stroke-width='2' stroke-dasharray='3,7' stroke-dashoffset='4' stroke-linecap='square'/%3e%3c/svg%3e");
        background-origin: content-box;
        background-repeat: no-repeat;
        border-radius: var(--spacing-2);
        padding: 1px;
    }
    &__skeleton {
        padding: var(--spacing-3);
        display: flex;
        flex-direction: column;
        gap: var(--spacing-3);
    }
    &__skeleton-header {
        display: flex;
        align-items: center;
        gap: var(--spacing-1);
        :deep(.compass-skeleton:last-child) {
            width: 120px;
        }
    }
    &__skeleton-row {
        display: flex;
        align-items: center;
        gap: var(--spacing-1);
        :deep(.compass-skeleton:first-child) {
            width: 24px;
            flex-shrink: 0;
        }
        :deep(.compass-skeleton:last-child) {
            flex: 1;
        }
    }
    &__empty {
        padding: var(--spacing-5) var(--spacing-4);
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--spacing-3);
        text-align: center;
    }
    &__empty-text {
        display: flex;
        flex-direction: column;
        align-items: center;
    }
    &__empty-title {
        margin: 0;
        color: var(--colors-common-text-standard-default);
    }
    &__empty-subtitle {
        margin: 0;
        color: var(--colors-common-text-standard-caption);
    }
}
</style>
